import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/i18n";
import Reveal from "./Reveal";

const IMG = "/work/solera-hormigon.jpeg";

const Manifesto = () => {
  const { t, lang } = useLang();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="nosotros"
      data-testid="manifesto-section"
      className="relative overflow-hidden bg-charcoal py-28 text-stone-300 lg:py-40"
    >
      <div className="bg-blueprint absolute inset-0 opacity-60" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-5 lg:grid-cols-2 lg:gap-24 lg:px-10">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <Reveal>
            <p
              data-testid="manifesto-eyebrow"
              className="font-mono text-[11px] uppercase tracking-[0.3em] text-terracotta"
            >
              {t.manifesto.eyebrow}
            </p>
            <h2
              data-testid="manifesto-heading"
              className="mt-5 font-display text-3xl font-medium leading-tight tracking-tight text-bone sm:text-4xl"
            >
              {t.manifesto.heading}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-stone-400">
              {t.manifesto.sub}
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-12 hidden lg:block">
            <div ref={imgRef} className="relative max-w-sm overflow-hidden">
              <div className="absolute -left-3 -top-3 h-full w-full border border-terracotta/50" aria-hidden="true" />
              <motion.img
                src={IMG}
                alt="Solera de hormigón en curso"
                style={{ y, scale: 1.15 }}
                className="relative aspect-[4/3] w-full object-cover"
              />
              <span className="absolute bottom-3 left-3 bg-charcoal/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-stone-300 backdrop-blur-sm">
                39.581°N · 0.324°W
              </span>
            </div>
          </Reveal>
        </div>

        <div data-testid="manifesto-chapters">
          {t.manifesto.chapters.map((c, i) => (
            <Reveal key={`${lang}-ch-${c.n}`} delay={i * 0.1}>
              <article
                data-testid={`manifesto-chapter-${c.n}`}
                className="group border-t border-white/10 py-10 transition-colors duration-500 first:border-t-0 first:pt-0 hover:bg-white/[0.03] lg:px-6 lg:first:pt-4"
              >
                <div className="flex items-baseline gap-6">
                  <span className="font-mono text-sm font-medium text-terracotta">
                    {c.n}
                  </span>
                  <div>
                    <h3 className="font-display text-2xl font-medium text-bone transition-colors duration-500 group-hover:text-terracotta sm:text-3xl">
                      {c.title}
                    </h3>
                    <p className="mt-4 max-w-lg text-sm leading-relaxed text-stone-400">
                      {c.desc}
                    </p>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
